import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useDispatch } from 'react-redux';

import { addNotification } from 'loot-core/client/actions';

import { useNavigate } from '../../hooks/useNavigate';
import { useResponsive } from '../../ResponsiveProvider';
import { theme } from '../../style';
import { useProgressChecklist } from './ProgressChecklistContext';
import { SvgTrophy, SvgList, SvgClose } from '../../icons/v1';
import { Button } from '../common/Button';
import { Text } from '../common/Text';
import { View } from '../common/View';

const SNOOZE_OPTIONS = [
  { label: 'Remind me tomorrow', durationMs: 24 * 60 * 60 * 1000 },
  { label: 'Remind me in 3 days', durationMs: 3 * 24 * 60 * 60 * 1000 },
  { label: 'Remind me in a week', durationMs: 7 * 24 * 60 * 60 * 1000 },
] as const;

interface ProgressChecklistFloatingIconProps {
  onOpenChecklist: () => void;
}

export function ProgressChecklistFloatingIcon({ onOpenChecklist }: ProgressChecklistFloatingIconProps) {
  const { state, hideChecklist, dismissChecklist, restoreChecklist } = useProgressChecklist();
  const { isNarrowWidth } = useResponsive();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [isHovered, setIsHovered] = useState(false);
  const [showPulse, setShowPulse] = useState(false);
  const [showSnoozeMenu, setShowSnoozeMenu] = useState(false);
  const snoozeMenuRef = useRef<HTMLDivElement>(null);

  // Memoize calculations to prevent unnecessary re-renders
  const progressPercentage = useMemo(() => 
    (state.completedCount / state.totalCount) * 100, 
    [state.completedCount, state.totalCount]
  );
  
  const isNearCompletion = useMemo(() => 
    progressPercentage >= 80, 
    [progressPercentage]
  );

  // Show pulse animation when a new task is completed
  useEffect(() => {
    if (state.completedCount > 0) {
      setShowPulse(true);
      const timer = setTimeout(() => setShowPulse(false), 2000);
      return () => clearTimeout(timer);
    }
  }, [state.completedCount]);

  // Close snooze menu when clicking outside
  useEffect(() => {
    if (!showSnoozeMenu) return;
    const handleClickOutside = (e: MouseEvent) => {
      if (snoozeMenuRef.current && !snoozeMenuRef.current.contains(e.target as Node)) {
        setShowSnoozeMenu(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showSnoozeMenu]);

  // Memoize hover handlers
  const handleMouseEnter = useCallback(() => setIsHovered(true), []);
  const handleMouseLeave = useCallback(() => setIsHovered(false), []);

  // Don't show if checklist is hidden (but keep it visible when completed)
  if (!state.showChecklist) {
    return null;
  }

  // Dismiss button is a separate fixed element to avoid stacking context conflicts
  // with the blue bubble. Position it at the bubble's top-left corner (offset -8px each).
  // Formula: bubbleMargin + bubbleSize - half_dismiss_button(12) - dismiss_offset(8) = margin + size - 16
  const bubbleSize = isNarrowWidth ? 50 : 60;
  const bubbleMargin = isNarrowWidth ? 20 : 30;
  const dismissAnchor = bubbleMargin + bubbleSize - 16;

  return (
    <>
      {/* Main bubble */}
      <View
        style={{
          position: 'fixed',
          bottom: bubbleMargin,
          right: bubbleMargin,
          zIndex: 9999,
        }}
      >
        {/* Progress Tooltip */}
        {isHovered && (
          <View
            style={{
              position: 'absolute',
              bottom: '100%',
              right: 0,
              marginBottom: 8,
              backgroundColor: theme.modalBackground,
              border: `1px solid ${theme.modalBorder}`,
              borderRadius: 8,
              padding: '12px 16px',
              minWidth: 200,
              boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
            }}
          >
            <Text
              style={{
                fontSize: 14,
                fontWeight: 600,
                color: theme.pageText,
                marginBottom: 8,
              }}
            >
              {state.isCompleted ? '🎉 All Tasks Complete!' : 'Onboarding Progress'}
            </Text>
            <Text
              style={{
                fontSize: 12,
                color: theme.tableText,
                marginBottom: 8,
              }}
            >
              {state.isCompleted
                ? 'Congratulations! You\'ve completed all onboarding tasks. Click the × to remove this checklist.'
                : `${state.completedCount} of ${state.totalCount} tasks completed`
              }
            </Text>
            <View
              style={{
                width: '100%',
                height: 6,
                backgroundColor: theme.tableBackground,
                borderRadius: 3,
                overflow: 'hidden',
              }}
            >
              <View
                style={{
                  width: `${state.isCompleted ? 100 : progressPercentage}%`,
                  height: '100%',
                  backgroundColor: state.isCompleted ? '#4CAF50' : isNearCompletion ? '#4CAF50' : '#2196F3',
                  transition: 'width 0.3s ease, background-color 0.3s ease',
                }}
              />
            </View>
          </View>
        )}

        {/* Floating Icon Button */}
        <Button
          type="primary"
          style={{
            width: bubbleSize,
            height: bubbleSize,
            borderRadius: '50%',
            padding: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: state.isCompleted ? '#4CAF50' : isNearCompletion ? '#4CAF50' : '#2196F3',
            border: 'none',
            boxShadow: showPulse
              ? '0 0 0 0 rgba(33, 150, 243, 0.7), 0 4px 12px rgba(0, 0, 0, 0.15)'
              : '0 4px 12px rgba(0, 0, 0, 0.15)',
            animation: showPulse ? 'pulse 2s infinite' : 'none',
            transform: isHovered ? 'scale(1.1)' : 'scale(1)',
            transition: 'transform 0.2s cubic-bezier(0.4, 0, 0.2, 1), background-color 0.2s ease',
          }}
          onClick={onOpenChecklist}
          onMouseEnter={handleMouseEnter}
          onMouseLeave={handleMouseLeave}
        >
          {state.isCompleted ? (
            <SvgTrophy
              style={{
                width: isNarrowWidth ? 24 : 28,
                height: isNarrowWidth ? 24 : 28,
                color: '#FFD700',
              }}
            />
          ) : isNearCompletion ? (
            <SvgTrophy
              style={{
                width: isNarrowWidth ? 24 : 28,
                height: isNarrowWidth ? 24 : 28,
                color: 'white',
              }}
            />
          ) : (
            <SvgList
              style={{
                width: isNarrowWidth ? 24 : 28,
                height: isNarrowWidth ? 24 : 28,
                color: 'white',
              }}
            />
          )}
        </Button>

        {/* Progress Badge */}
        {state.completedCount > 0 && !state.isCompleted && (
          <View
            style={{
              position: 'absolute',
              top: -8,
              right: -8,
              backgroundColor: '#FF5722',
              color: 'white',
              borderRadius: '50%',
              width: isNarrowWidth ? 20 : 24,
              height: isNarrowWidth ? 20 : 24,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: isNarrowWidth ? 10 : 12,
              fontWeight: 600,
              border: '2px solid white',
              boxShadow: '0 2px 4px rgba(0, 0, 0, 0.2)',
            }}
          >
            <Text
              style={{
                color: 'white',
                fontSize: isNarrowWidth ? 10 : 12,
                fontWeight: 600,
              }}
            >
              {state.completedCount}
            </Text>
          </View>
        )}

        <style>
          {`
            @keyframes pulse {
              0% { box-shadow: 0 0 0 0 rgba(33, 150, 243, 0.7); }
              70% { box-shadow: 0 0 0 10px rgba(33, 150, 243, 0); }
              100% { box-shadow: 0 0 0 0 rgba(33, 150, 243, 0); }
            }
          `}
        </style>
      </View>

      {/* Dismiss button + snooze menu — separate fixed element, fully independent stacking context */}
      <View
        ref={snoozeMenuRef}
        style={{
          position: 'fixed',
          bottom: dismissAnchor,
          right: dismissAnchor,
          zIndex: 10000,
          width: 24,
          height: 24,
        }}
      >
        {/* Snooze dropdown — right edge aligned with the blue bubble's right edge */}
        {showSnoozeMenu && (
          <View
            style={{
              position: 'absolute',
              bottom: 'calc(100% + 8px)',
              right: -(bubbleSize - 16),
              backgroundColor: theme.modalBackground,
              border: `1px solid ${theme.modalBorder}`,
              borderRadius: 8,
              overflow: 'hidden',
              boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
              minWidth: 180,
            }}
          >
            {SNOOZE_OPTIONS.map(option => (
              <Button
                key={option.label}
                type="bare"
                style={{
                  width: '100%',
                  padding: '8px 12px',
                  textAlign: 'left',
                  borderRadius: 0,
                  fontSize: 13,
                  color: theme.pageText,
                }}
                onClick={(e) => {
                  e.stopPropagation();
                  dismissChecklist(option.durationMs);
                  setShowSnoozeMenu(false);
                }}
              >
                {option.label}
              </Button>
            ))}
            <View
              style={{
                height: 1,
                backgroundColor: theme.menuBorder,
                margin: '4px 0',
              }}
            />
            <Button
              type="bare"
              style={{
                width: '100%',
                padding: '8px 12px',
                textAlign: 'left',
                borderRadius: 0,
                fontSize: 13,
                color: theme.errorText,
              }}
              onClick={(e) => {
                e.stopPropagation();
                hideChecklist();
                setShowSnoozeMenu(false);
                dispatch(
                  addNotification({
                    type: 'message',
                    message:
                      'Onboarding checklist hidden. You can always change this in the [settings](#settings) page.',
                    messageActions: {
                      settings: () => navigate('/settings'),
                    },
                    timeout: 6000,
                    button: {
                      title: 'Undo',
                      action: () => restoreChecklist(),
                    },
                  }),
                );
              }}
            >
              Don't show this again
            </Button>
          </View>
        )}

        <Button
          type="bare"
          hoveredStyle={{
            backgroundColor: state.isCompleted ? '#c82333' : theme.buttonNormalBackgroundHover,
          }}
          style={{
            width: 24,
            height: 24,
            borderRadius: '50%',
            backgroundColor: state.isCompleted ? '#dc3545' : theme.buttonNormalBackground,
            border: '2px solid white',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: 'pointer',
            boxShadow: '0 2px 4px rgba(0, 0, 0, 0.3)',
          }}
          onClick={(e) => {
            e.stopPropagation();
            if (state.isCompleted) {
              hideChecklist();
            } else {
              setShowSnoozeMenu(v => !v);
            }
          }}
          title={state.isCompleted ? 'Remove checklist permanently' : 'Dismiss for a while'}
        >
          <SvgClose
            style={{
              width: 12,
              height: 12,
              color: state.isCompleted ? 'white' : theme.pageText,
            }}
          />
        </Button>
      </View>
    </>
  );
}
